import { createRouter, createWebHistory } from "vue-router";
import Profile from "../views/Profile.vue";
import Callback from "../views/Callback.vue";
import Landing from "../views/Landing.vue";
import Overview from "../views/Overview.vue";
import { useAuthService } from "../services/auth.service";
import { useUserRole } from "../composables/useUserRole";
import { watch } from "vue";
import { safeTarget } from "../utils/navigation";

const routes = [
  {
    path: "/",
    name: "Landing",
    component: Landing,
    meta: { requiresAuth: false }
  },
  {
    path: "/overview",
    name: "Overview",
    component: Overview,
    meta: { requiresAuth: true } // Protected route
  },
  {
    path: "/devices/:id",
    name: "DeviceDetails",
    component: () => import("../views/DeviceDetails.vue"),
    meta: { requiresAuth: true } // Protected route
  },
  {
    path: "/admin/users",
    name: "AdminUsers",
    component: () => import("../views/AdminUsers.vue"),
    meta: { requiresAuth: true, roles: ['admin', 'support'] } // Protected route for admin/support
  },
  {
    path: "/profile",
    name: "Profile",
    component: Profile,
    meta: { requiresAuth: true } // Protected route
  },
  {
    path: "/signin",
    name: "Signin",
    beforeEnter: (to) => {
      const { login } = useAuthService();
      // Store the intended destination if any
      const redirect = safeTarget(to.query.redirect);
      login({ appState: { targetUrl: redirect } });
    },
    meta: { requiresAuth: false }
  },
  {
    path: "/signup",
    name: "Signup",
    beforeEnter: (to) => {
      const { signup } = useAuthService();
      // Store the intended destination if any
      const redirect = safeTarget(to.query.redirect);
      signup({ appState: { targetUrl: redirect } });
    },
    meta: { requiresAuth: false }
  },
  {
    path: "/callback",
    name: "Callback",
    component: Callback,
    meta: { requiresAuth: false } // Public route for Auth0 callback
  },
  {
    path: "/notifications",
    name: "Notifications",
    component: () => import("../views/Notifications.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/subscription",
    name: "Subscription",
    component: () => import("../views/Subscription.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/admin/subscriptions",
    name: "AdminSubscriptions",
    component: () => import("../views/AdminSubscriptions.vue"),
    meta: { requiresAuth: true, roles: ['admin', 'support'] }
  },
  {
    path: "/admin/devices",
    name: "AdminDevicesRedirect",
    meta: { requiresAuth: true, roles: ['admin', 'support'] },
    beforeEnter: () => ({ name: 'Overview' })
  },
    {
    path: "/admin/monitoring",
    name: "AdminMonitoring",
    component: () => import("../views/AdminMonitoring.vue"),
    meta: { requiresAuth: true, roles: ['admin', 'support'] }
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: "active",
});

const waitUntilReady = (isLoading) => new Promise(resolve => {
  if (!isLoading.value) return resolve();
  const stop = watch(isLoading, value => {
    if (!value) { stop(); resolve(); }
  });
});

router.beforeEach(async (to) => {
  if (!to.matched.some(record => record.meta.requiresAuth)) return true;
  const { isAuthenticated, isLoading, login } = useAuthService();
  await waitUntilReady(isLoading);
  if (!isAuthenticated.value) {
    const targetUrl = safeTarget(to.fullPath);
    sessionStorage.setItem('authTargetUrl', targetUrl);
    await login({ appState: { targetUrl } });
    return false;
  }
  const restricted = to.matched.find(record => record.meta.roles);
  if (!restricted) return true;
  const { userRole, fetchUserRole } = useUserRole();
  await fetchUserRole();
  return restricted.meta.roles.includes(userRole.value) || { name: 'Overview' };
});

export default router;
